import { IBackgroundCheckRequestResponse } from "@wingspanhq/background-checks/dist/lib/interfaces";
import { WSPageToolbarAction } from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { openInNewTab } from "../../../../../../shared/utils/openInNewTab";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementTableCallback
} from "../../../../types";
import { getRequirementEngagementsTableCellProps } from "../../../../utils";
import { ReportsInfoWidget } from "../../components/ReportsInfoWidget";
import { getBackgroundCheckPayerRequirementStatus } from "../helpers/getBackgroundCheckPayerRequirementStatus";
import { getBackgroundCheckProgressSteps } from "../helpers/getBackgroundCheckProgressSteps";
import { getBackgroundChecksRequirementName } from "../helpers/getBackgroundChecksRequirementName";
import { payeeBackgroundCheckRenderCardDescription } from "./payeeBackgroundCheckRenderCardDescription";

export const usePayeeBackgroundChecksConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  IBackgroundCheckRequestResponse | undefined
> => {
  const getManageReportAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    backgroundChecksRequest: IBackgroundCheckRequestResponse | undefined
  ) => {
    if (backgroundChecksRequest?.vendorMetadata.manageReportUrl) {
      return async () => {
        openInNewTab(backgroundChecksRequest.vendorMetadata.manageReportUrl!);
      };
    }
  };

  const getOpenReportAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    backgroundChecksRequest: IBackgroundCheckRequestResponse | undefined
  ) => {
    if (backgroundChecksRequest?.vendorMetadata.viewReportUrl) {
      return async () => {
        openInNewTab(backgroundChecksRequest.vendorMetadata.viewReportUrl!);
      };
    }
  };

  return {
    isActionRequired: requirement => {
      return false;
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      backgroundChecksRequest
    ) => {
      return backgroundChecksRequest?.status
        ? getBackgroundCheckProgressSteps(backgroundChecksRequest.status)
        : undefined;
    },
    requirementDetailsWidgets: (
      requirement,
      context,
      backgroundChecksRequest
    ) => {
      if (backgroundChecksRequest?.backgroundCheckRequestSettingsId) {
        return (
          <ReportsInfoWidget
            backgroundCheckRequestSettingsId={
              backgroundChecksRequest.backgroundCheckRequestSettingsId
            }
          />
        );
      }

      return null;
    },
    requirementStatusAlert: (requirement, context, backgroundChecksRequest) => {
      return undefined;
    },
    requirementName: getBackgroundChecksRequirementName,
    requirementDetails: (requirement, context, backgroundChecksRequest) => {
      const manageReportAction = getManageReportAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      const openReportAction = getOpenReportAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      return {
        header: {
          label: {
            text: getBackgroundChecksRequirementName(
              requirement,
              context,
              backgroundChecksRequest
            )
          }
        },
        children: payeeBackgroundCheckRenderCardDescription(
          requirement,
          backgroundChecksRequest
        ),
        data: [],
        actions:
          manageReportAction || openReportAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Manage Report",
                    onAsyncClick: manageReportAction,
                    kind: "Secondary",
                    visible: !!manageReportAction
                  },
                  {
                    label: "View Report",
                    onAsyncClick: openReportAction,
                    kind: "Secondary",
                    visible: !!openReportAction
                  }
                ]
              }
            : undefined
      };
    },
    toolbarActions: (requirement, context, backgroundChecksRequest) => {
      const actions: WSPageToolbarAction[] = [];

      const manageReportAction = getManageReportAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      const openReportAction = getOpenReportAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      if (manageReportAction) {
        actions.push({
          name: "Manage",
          onAsyncClick: manageReportAction,
          label: "Manage Report",
          icon: "open-tab",
          buttonKind: "Secondary"
        });
      }

      if (openReportAction) {
        actions.push({
          name: "View",
          onAsyncClick: openReportAction,
          label: "View Report",
          icon: "open-tab",
          buttonKind: "Secondary"
        });
      }

      return actions;
    },
    nameCell: (requirement, context, backgroundChecksRequest) => {
      return {
        text: getBackgroundChecksRequirementName(
          requirement,
          context,
          backgroundChecksRequest
        )
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType
      }),
    statusCell: (requirement, context, backgroundChecksRequest) => {
      return getBackgroundCheckPayerRequirementStatus(
        requirement,
        context,
        backgroundChecksRequest
      );
    },
    engagementsCell: getRequirementEngagementsTableCellProps
  };
};
